/* --------------------------
Includes code from SubScatter Vertex Shader:

Fake sub-surface scatter lighting shader by InvalidPointer 2008.
http://www.gamedev.net/community/forums/topic.asp?topic_id=481494
 
HLSL > GLSL translation
toneburst 2008

GLSL > THREE.JS Comaptible translation
thisispete 2021
-------------------------- */

uniform vec3 baseColor;

uniform samplerCube baseTexture;
uniform samplerCube env;
uniform sampler2D matCapText;

vec3 specColor = vec3(0.92);
vec3 lightPosition = vec3(0.0, 0.0, -0.1);
float materialThickness = 0.78;
float specPower = 0.1;
float ior = 1.67;
int sssPow = 2;
float sssMix = 0.33;
int reflectPow = 1;
float reflectMix = 0.18;
float matCapMix = 0.18;
float tfMix = 2.0;
vec3 tfLightPos = vec3(0.0, -0.1, -0.65);
float tfThickness = 1234.0; //# Thin film thickness (in nm) 0 3000 
float externalIOR = 1.82; //# External (air) refractive index 0.2 3
float thinfilmIOR = 2.11; //# Layer (thin film) refractive index 0.2 3
float internalIOR = 2.37; //# Internal (object) refractive index 0.2 3
float tfN = 1.6; //# Blinn-Phong microfacet exponent 1 1000

varying vec3 vFakeUVW;
varying vec4 vPosition;
varying vec3 vNorm; 
varying vec3 vEyeVec;
varying vec3 vWorldNormal;
varying vec3 vNormMod;
varying vec4 vPosProj;
 
  
const float PI = 3.14159265; 

float halfLambert(in vec3 vect1, in vec3 vect2) {
    float product = dot(vect1, vect2);
    return product * 0.5 + 0.5;
}
 
float blinnPhongSpecular(in vec3 normalVec, in vec3 lightVec, in float specPower) {
    vec3 halfAngle = normalize(normalVec + lightVec);
    return pow(clamp(0.0,1.0,dot(normalVec, halfAngle)), specPower);
}
  

vec4 subScatter(in vec3 vertPos, in vec3 wNorm) {
    float RimScalar = 0.2;
    float attenuation = .9 * (1.0 / distance(lightPosition, vertPos));
    vec3 eVec = normalize(-vertPos);
    vec3 lVec = normalize( lightPosition - vertPos);
     
    vec4 dotLN = vec4(halfLambert(lVec, wNorm) * attenuation);
     
    vec3 indirectLightComponent = vec3(materialThickness * max(0.0, dot(-wNorm, lVec)));
    indirectLightComponent += materialThickness * halfLambert(-eVec, lVec);
    indirectLightComponent *= attenuation;
     
    vec3 rim = vec3(1.0 - max(0.0, dot(wNorm, eVec)));
    rim *= rim;
    rim *= max(0.0, dot(wNorm, lVec)) * specColor.rgb;
     
    vec4 finalCol = dotLN + vec4(indirectLightComponent, 1.0);
    finalCol.rgb += (rim * RimScalar * attenuation * finalCol.a);
    finalCol.rgb += vec3(blinnPhongSpecular(wNorm, lVec, specPower) * attenuation * specColor * finalCol.a * 0.05);
    finalCol.rgb *= vec3(1.0);

    return finalCol;    
}



/* Amplitude reflection coefficient (s-polarized) */ 
float rs(float n1, float n2, float cosI, float cosT) { 
    return (n1 * cosI - n2 * cosT) / (n1 * cosI + n2 * cosT); 
} 

/* Amplitude reflection coefficient (p-polarized) */ 
float rp(float n1, float n2, float cosI, float cosT) { 
    return (n2 * cosI - n1 * cosT) / (n1 * cosT + n2 * cosI); 
} 

/* Amplitude transmission coefficient (s-polarized) */ 

float ts(float n1, float n2, float cosI, float cosT) { 
    return 2.0 * n1 * cosI / (n1 * cosI + n2 * cosT); 
} 

/* Amplitude transmission coefficient (p-polarized) */ 
float tp(float n1, float n2, float cosI, float cosT) { 
    return 2.0 * n1 * cosI / (n1 * cosT + n2 * cosI); 
} 

/* Pass the incident cosine. */ 
vec3 FresnelCoating(float cos0) { 
    /* Precompute the reflection phase changes (depends on IOR) */ 
    float delta10 = (thinfilmIOR < externalIOR) ? PI : 0.0; 
    float delta12 = (thinfilmIOR < internalIOR) ? PI : 0.0; 
    float delta = delta10 + delta12; 
    
    /* Calculate the thin film layer (and transmitted) angle cosines. */ 
    float sin1 = pow(externalIOR / thinfilmIOR, 2.0) * (1.0 - pow(cos0, 2.0)); 
    float sin2 = pow(externalIOR / internalIOR, 2.0) * (1.0 - pow(cos0, 2.0)); 
    if ((sin1 > 1.0) || (sin2 > 1.0)) return vec3(1.0); 
    
    /* Account for TIR. */ 
    float cos1 = sqrt(1.0 - sin1), cos2 = sqrt(1.0 - sin2); 

    /* Calculate the interference phase change. */ 
    vec3 phi = vec3(2.0 * thinfilmIOR * tfThickness * cos1); 
    phi *= 2.0 * PI / vec3(650.0, 510.0, 475.0); 
    phi += delta; 
    
    /* Obtain the various Fresnel amplitude coefficients. */ 
    float alpha_s = rs(thinfilmIOR, externalIOR, cos1, cos0) * rs(thinfilmIOR, internalIOR, cos1, cos2); 
    float alpha_p = rp(thinfilmIOR, externalIOR, cos1, cos0) * rp(thinfilmIOR, internalIOR, cos1, cos2); 
    float beta_s = ts(externalIOR, thinfilmIOR, cos0, cos1) * ts(thinfilmIOR, internalIOR, cos1, cos2); 
    float beta_p = tp(externalIOR, thinfilmIOR, cos0, cos1) * tp(thinfilmIOR, internalIOR, cos1, cos2); 
    
    /* Calculate the s- and p-polarized intensity transmission coefficient. */ 
    vec3 ts = pow(beta_s, 2.0) / (pow(alpha_s, 2.0) - 2.0 * alpha_s * cos(phi) + 1.0); 
    vec3 tp = pow(beta_p, 2.0) / (pow(alpha_p, 2.0) - 2.0 * alpha_p * cos(phi) + 1.0); 
    
    /* Calculate the transmitted power ratio for medium change. */ 
    float beamRatio = (internalIOR * cos2) / (externalIOR * cos0); 
    
    /* Calculate the average reflectance. */ 
    return 1.0 - beamRatio * (ts + tp) * 0.5; 
} 

vec4 thinFilm(in vec3 vertexPos, in vec3 vertexNormal) { 
    vec3 H = normalize(tfLightPos + normalize(vertexPos)); 
    float val = pow(max(0.0, dot(vertexNormal, H)), tfN); 
    return vec4(vec3(val) * FresnelCoating(dot(vertexPos, H)), 1.0); 
} 

vec4 matCap(in vec3 normal, in vec3 eyeVec) {
  vec3 r = reflect( eyeVec, normal );
  float m = 2. * sqrt( pow( r.x, 2. ) + pow( r.y, 2. ) + pow( r.z + 1., 2. ) );
  vec2 vN = r.xy / m + .5;

  return texture2D( matCapText, vN );
}

void main() {

    vec4 baseColor = vec4(baseColor, 1);
    vec4 textureMapcolor = textureCube(baseTexture, vFakeUVW);
    vec4 refractColor = textureCube(env, refract(vEyeVec, vNormMod, 1.0 / ior));
    vec4 reflectColor = pow(textureCube(env, reflect(vEyeVec, vNormMod )), vec4(reflectPow));
    vec4 sssColor = pow(subScatter(vPosProj.xyz, vNorm), vec4(sssPow));
    vec4 matCapColor = matCap(vNorm, vWorldNormal - vEyeVec);
    vec4 thinFilmColor = thinFilm(vPosition.xyz, vNormMod);

    vec4 color = baseColor;
    color = mix(color, textureMapcolor, 0.55);
    color = mix(color, refractColor, vWorldNormal.z *.8 + .2);
    color = mix(color, sssColor, sssMix);
    color = mix(color, reflectColor, reflectMix); 
    color = mix(color, color + matCapColor, matCapMix);
    color = mix(color, color + thinFilmColor, vWorldNormal.x *.5 +tfMix/2.0); 

    gl_FragColor = color;
}
